'use client'
import * as React from 'react'

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from 'ui/select'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { IoIosArrowDown } from 'react-icons/io'
import { cn } from 'app/lib/utils'
import { getLocaleDisplayCode } from 'app/lib/localeDisplayCode'

// const flags: Record<string, string> = {
//     en: '🇬🇧',
//     es: '🇪🇸',
//     it: '🇮🇹',
//     fr: '🇫🇷',
//     ar: '🇸🇦'
// }

const flags: Record<string, string> = {
    en: 'https://cdn.jsdelivr.net/npm/flag-icons/flags/4x3/gb.svg',
    es: 'https://cdn.jsdelivr.net/npm/flag-icons/flags/4x3/es.svg',
    it: 'https://cdn.jsdelivr.net/npm/flag-icons/flags/4x3/it.svg',
    fr: 'https://cdn.jsdelivr.net/npm/flag-icons/flags/4x3/fr.svg',
    ar: 'https://cdn.jsdelivr.net/npm/flag-icons/flags/4x3/sa.svg',
    pt: 'https://cdn.jsdelivr.net/npm/flag-icons/flags/4x3/pt.svg',
    uk: 'https://cdn.jsdelivr.net/npm/flag-icons/flags/4x3/ua.svg'
}

interface LanguageSwitcherProps {
    triggerClassName?: string
    contentClassName?: string
    selectedBackgroundClassName?: string
    hoverBackgroundClassName?: string
}

function LanguageSwitcher({
    triggerClassName,
    contentClassName,
    selectedBackgroundClassName,
    hoverBackgroundClassName
}: LanguageSwitcherProps) {
    const router = useRouter()
    const params = useParams()
    const pathname = usePathname()
    const [selected, setSelected] = React.useState<string>(
        Array.isArray(params?.lang) ? params.lang[0] : params?.lang || 'en'
    )
    const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false)

    const getLabel = (lang: string) => (
        <div className='flex items-center gap-2'>
            <img src={flags[lang]} alt={`${lang} flag`} className='w-5 h-5' /> {/* Add flag image */}
            <span>{getLocaleDisplayCode(lang)}</span>
        </div>
    )

    const changeLanguage = (newLang: string) => {
        setSelected(newLang)
        const newPathname = pathname.replace(`/${params.lang}`, `/${newLang}`)
        router.push(newPathname)
    }

    const handleOpenChange = (open: boolean) => setIsDropdownOpen(open)

    return (
        <Select value={selected} open={isDropdownOpen} onValueChange={changeLanguage} onOpenChange={handleOpenChange}>
            <SelectTrigger
                selectIcon={
                    <IoIosArrowDown
                        className={cn(
                            'text-foreground group-hover:text-primary w-5 h-5 transition-all duration-500 opacity-50',
                            isDropdownOpen ? '-rotate-180' : 'rotate-0'
                        )}
                    />
                }
                className={cn(
                    'gap-2 justify-center h-12 md:max-w-full px-3 text-sm font-bold leading-5 text-center whitespace-nowrap rounded-lg border-2 relative flex',
                    triggerClassName
                )}
            >
                <SelectValue placeholder={getLabel(selected)} />
            </SelectTrigger>
            <SelectContent className={cn('rounded-lg border-2 glassmorphism z-[1000]', contentClassName)}>
                <SelectGroup>
                    {Object.keys(flags).map(lang => (
                        <SelectItem
                            key={lang}
                            value={lang}
                            className={cn(
                                'cursor-pointer',
                                selected === lang && selectedBackgroundClassName,
                                hoverBackgroundClassName
                            )}
                        >
                            {getLabel(lang)}
                        </SelectItem>
                    ))}
                </SelectGroup>
            </SelectContent>
        </Select>
    )
}

export default LanguageSwitcher
